import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'model/Api';
import {
  Role,
  Permission,
  permissions,
  createEmptyPermissions,
} from 'model/Role';
import { generalStyle } from 'helper/Style';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import OperationTrigger from 'component/part/OperationTrigger';
import H2 from 'component/part/H2';

const initialRole: Role = {
  id: 0,
  code: '',
  name: '',
  permissions: createEmptyPermissions(),
};

const style = {
  form: {
    ...generalStyle.form,
    ['& input[type="text"]']: {
      maxWidth: '32rem',
    },
  },
  permissionList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.8rem',
  },
  permissionItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    fontSize: '1.4rem',
    cursor: 'pointer',
  },
};

const RoleEdit: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const urlParams = useParams<{ id: string }>();
  const roleId = parseInt(urlParams.id || '');
  const [role, setRole] = useState<Role>(initialRole);

  const onChangeRole = <K extends keyof Role>(key: K, value: Role[K]) => {
    setRole({ ...role, [key]: value });
  };

  const togglePermission = (code: Permission) => {
    setRole({
      ...role,
      permissions: { ...role.permissions, [code]: !role.permissions[code] },
    });
  };

  const onSubmit = async () => {
    const result = await api.role.update(role);

    return result.ok && !!result.data;
  };

  useEffect(() => {
    (async () => {
      if (roleId) {
        const roleResponse = await api.role.get(roleId);
        if (roleResponse) {
          setRole(roleResponse);
        } else {
          setRole({ ...initialRole, id: roleId });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  return <Layout>
    <BackLink link='/role/list' label='役職一覧'></BackLink>
    <H2>{roleId ? '役職編集' : '役職登録'}</H2>
    <div css={style.form}>
      <FormContent label='役職名' required>
        <input
          type='text'
          value={role.name}
          onChange={(e) => onChangeRole('name', e.target.value)}
        />
      </FormContent>
      <FormContent label='権限'>
        <div css={style.permissionList}>
          {permissions.flatMap(({ code, label }) => code !== 'root' ? (
            <label key={code} css={style.permissionItem}>
              <input
                type='checkbox'
                checked={role.permissions[code]}
                onChange={() => togglePermission(code)}
              />
              <span>{label}</span>
            </label>
          ) : [])}
        </div>
      </FormContent>
      <OperationTrigger
        onSubmit={onSubmit}
        onOk={() => navigate('/role/list')}
        buttonText={roleId ? '更新' : '登録'}
        objectType='role'
        operationType={roleId ? 'edit' : 'create'}
      />
    </div>
  </Layout>;
};

export default RoleEdit;
